// src/services/adminApi.js
// ---------------------------------------------------------------------------
// Admin-only doctor/patient/user management API
// All endpoints require: Authorization: Bearer <token>  +  X-Tenant-ID header
//
// POST/GET/PUT/DELETE /api/admin/doctors[/:id]
// POST/GET/PUT/DELETE /api/admin/patients[/:id]
// GET                 /api/admin/users
// ---------------------------------------------------------------------------

import { API_BASE_URL as BASE_URL } from './apiBaseUrl';

const authHeaders = (token, tenantId) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
  'X-Tenant-ID': tenantId,
});

const parseError = async (res, fallback) => {
  const body = await res.json().catch(() => ({}));
  return new Error(body.message || `${fallback} (${res.status})`);
};

// ---------------------------------------------------------------------------
// Doctors
// ---------------------------------------------------------------------------
export const getAllDoctorsAdmin = async (token, tenantId) => {
  const res = await fetch(`${BASE_URL}/admin/doctors`, {
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw await parseError(res, 'Failed to fetch doctors');
  return res.json();
};

export const createDoctorAdmin = async (token, tenantId, payload) => {
  const res = await fetch(`${BASE_URL}/admin/doctors`, {
    method: 'POST',
    headers: authHeaders(token, tenantId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res, 'Failed to create doctor');
  return res.json();
};

export const updateDoctorAdmin = async (token, tenantId, id, payload) => {
  const res = await fetch(`${BASE_URL}/admin/doctors/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, tenantId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res, 'Failed to update doctor');
  return res.json();
};

export const deactivateDoctorAdmin = async (token, tenantId, id) => {
  const res = await fetch(`${BASE_URL}/admin/doctors/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw await parseError(res, 'Failed to deactivate doctor');
};

export const reactivateDoctorAdmin = async (token, tenantId, id) => {
  const res = await fetch(`${BASE_URL}/admin/doctors/${id}/reactivate`, {
    method: 'PUT',
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw await parseError(res, 'Failed to reactivate doctor');
};

// ---------------------------------------------------------------------------
// Patients
// ---------------------------------------------------------------------------
export const getAllPatientsAdmin = async (token, tenantId) => {
  const res = await fetch(`${BASE_URL}/admin/patients`, {
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw await parseError(res, 'Failed to fetch patients');
  return res.json();
};

export const createPatientAdmin = async (token, tenantId, payload) => {
  const res = await fetch(`${BASE_URL}/admin/patients`, {
    method: 'POST',
    headers: authHeaders(token, tenantId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res, 'Failed to create patient');
  return res.json();
};

export const updatePatientAdmin = async (token, tenantId, id, payload) => {
  const res = await fetch(`${BASE_URL}/admin/patients/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, tenantId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res, 'Failed to update patient');
  return res.json();
};

export const deactivatePatientAdmin = async (token, tenantId, id) => {
  const res = await fetch(`${BASE_URL}/admin/patients/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw await parseError(res, 'Failed to deactivate patient');
};

// ---------------------------------------------------------------------------
// Tenants (super-admin — no X-Tenant-ID header needed)
// POST /api/tenants   GET /api/tenants   PUT /api/tenants/:id
// ---------------------------------------------------------------------------
export const getAllTenantsAdmin = async (token) => {
  const res = await fetch(`${BASE_URL}/tenants`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw await parseError(res, 'Failed to fetch tenants');
  return res.json();
};

export const createTenantAdmin = async (token, payload) => {
  const res = await fetch(`${BASE_URL}/tenants`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res, 'Failed to create tenant');
  return res.json();
};

export const updateTenantAdmin = async (token, tenantId, payload) => {
  const res = await fetch(`${BASE_URL}/tenants/${tenantId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res, 'Failed to update tenant');
  return res.json();
};

// ---------------------------------------------------------------------------
// Blood group options — used by the patient form select
// ---------------------------------------------------------------------------
export const BLOOD_GROUPS = ['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'];

// ---------------------------------------------------------------------------
// Language options — used by the doctor form multiselect
// ---------------------------------------------------------------------------
export const LANGUAGE_OPTIONS = ['English', 'Hindi', 'Telugu', 'Tamil', 'Kannada', 'Marathi', 'Urdu'];